import type { Schemas } from '@suvoda-uat/contracts';

/** A real `TestCase.selectionDecision`, straight from the contract. */
export type SelectionDecisionData = Schemas['TestCase']['selectionDecision'];

/** The only field Template Match needs from a Content Item — its Origin. */
type OriginCarrier = Pick<Schemas['TestCase']['content']['rows'][number], 'origin'>;

/**
 * Per-Origin Content Item counts for one Test Case. The basis of the
 * **Template Match** readout: unlike the engine's Selection Score (which
 * measures how strongly the TRIAL'S REQUIREMENTS matched historical trials
 * containing the case, and is uncorrelated with content provenance), this is
 * derived from the served content itself.
 */
export type OriginSummary = {
  readonly template: number;
  readonly edited: number;
  readonly novel: number;
  /** #123: grounded items (copy-with-substitution + citation-verified). */
  readonly grounded: number;
  readonly total: number;
};

export const summariseOrigins = (rows: ReadonlyArray<OriginCarrier>): OriginSummary => {
  const count = (origin: OriginCarrier['origin']): number =>
    rows.filter((row) => row.origin === origin).length;
  return {
    template: count('template'),
    edited: count('edited'),
    novel: count('novel'),
    grounded: count('grounded'),
    total: rows.length,
  };
};

/**
 * Template Match % (0–100): the share of the case's Content Items that are
 * VERBATIM from the template (`origin === 'template'`). Edited items count
 * toward the breakdown label, not the headline match. Empty content reads 0.
 */
export const templateMatchPct = (summary: OriginSummary): number =>
  summary.total === 0 ? 0 : Math.round((summary.template / summary.total) * 100);

/** Human-readable breakdown behind the %: "10 of 12 items verbatim from template · 1 edited · 1 novel". */
export const templateMatchLabel = (summary: OriginSummary): string => {
  if (summary.total === 0) return 'No content items';
  const parts = [`${summary.template} of ${summary.total} items verbatim from template`];
  if (summary.edited > 0) parts.push(`${summary.edited} edited`);
  if (summary.grounded > 0) parts.push(`${summary.grounded} grounded`);
  if (summary.novel > 0) parts.push(`${summary.novel} novel`);
  return parts.join(' · ');
};

/** "26/27" → "26 of 27"; anything else rides through unchanged. */
const frequencyPhrase = (frequency: string): string => {
  const match = /^(\d+)\/(\d+)$/.exec(frequency);
  return match ? `${match[1]} of ${match[2]}` : frequency;
};

/**
 * One-line WHY for the engine's Selection Decision, in domain language. The
 * raw score is deliberately not shown: it is normalised against the best
 * case in the batch and damped by historical frequency, so the absolute
 * number misleads (ubiquitous boilerplate cases legitimately score lowest —
 * that is exactly why `frequency_auto_include` exists).
 */
export const selectionReason = (selectionDecision: SelectionDecisionData): string => {
  switch (selectionDecision.source) {
    case 'override_include':
      return 'Included by a project override.';
    case 'override_exclude':
      return 'Excluded by a project override.';
    case 'frequency_auto_include':
      return selectionDecision.historicalFrequency === undefined
        ? 'Standard case — included in nearly all historical trials.'
        : `Standard case — included in ${frequencyPhrase(selectionDecision.historicalFrequency)} historical trials.`;
    default:
      switch (selectionDecision.decision) {
        case 'selected':
          return "This trial's requirements match historical trials that used this case.";
        case 'excluded':
          return "This trial's requirements rarely matched trials that used this case.";
        default:
          return 'Weak requirements match — review whether this case applies to this trial.';
      }
  }
};
